import { Loader } from 'lucide-react';
import { Button } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

// A factory rather than a module-scope table: building it at import time would bake in
// whatever locale was active then.
const buildStepLabels = (t: TFunction<'contentTest'>) => [
  t('contentTest.runProgressLeftRail.analyzingVisuals'),
  t('contentTest.runProgressLeftRail.gatheringFeedback'),
  t('contentTest.runProgressLeftRail.generatingReport'),
];

interface RunProgressLeftRailProps {
  runIsError: boolean;
  onRetry: () => void;
  onCancelClick: () => void;
}

const StepRow = ({ label, index }: { label: string; index: number }) => (
  <div className="flex items-center gap-3 rounded-lg border border-border p-3">
    <div
      data-testid={`run-step-row-icon-${index}`}
      data-state="pending"
      className="flex h-5 w-5 items-center justify-center"
    >
      <Loader size={24} className="text-primary shrink-0 animate-spin" />
    </div>
    <span className="text-sm text-foreground">{label}</span>
  </div>
);

export const RunProgressLeftRail = ({
  runIsError,
  onRetry,
  onCancelClick,
}: RunProgressLeftRailProps) => {
  const { t } = useTranslation('contentTest');
  return (
    <div className="flex h-full flex-col gap-4">
      <p className="text-base font-medium text-foreground">
        {t('contentTest.runProgressLeftRail.runAndGenerateResults')}
      </p>
      {runIsError && (
        <div
          role="alert"
          className="bg-destructive/10 border border-destructive rounded-lg p-3 flex items-center justify-between gap-3"
        >
          <span className="text-sm text-destructive">
            {t('contentTest.runProgressLeftRail.couldnTStartScoring')}
          </span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t('contentTest.runProgressLeftRail.retry')}
          </Button>
        </div>
      )}
      <div className="flex flex-col gap-3">
        {buildStepLabels(t).map((label, i) => (
          <StepRow key={label} label={label} index={i} />
        ))}
      </div>
      {!runIsError && (
        <div className="mt-auto pt-4">
          <Button variant="outline" onClick={onCancelClick} className="w-full">
            {t('contentTest.runProgressLeftRail.cancel')}
          </Button>
        </div>
      )}
    </div>
  );
};

RunProgressLeftRail.displayName = 'RunProgressLeftRail';
